import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { store } from '../../app/store';
import {
  SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE,
  useGetConfiguredFinanceBccQuery,
  useLazyGetSimpleOutboundStatusQuery,
  useSendSimpleOutboundMutation,
  waitForSimpleOutboundResult,
  type ComposeRecipient,
} from '../grid/api/simpleOutboundApi';
import { invalidateGridDataCache } from '../grid/api/invalidateGridDataCache';
import { extractErrorMessage } from '../../utils/errorUtils';
import { FinanceEmailComposeAttachments } from '../grid/components/dialogs/FinanceEmailComposeAttachments';
import { FinanceEmailComposeFields } from '../grid/components/dialogs/FinanceEmailComposeFields';
import {
  DRAGGABLE_DIALOG_TITLE_ID,
  DraggableResizablePaper,
} from '../grid/components/dialogs/DraggableResizablePaper';
import { countRecipientAddresses, formatRecipientsSummary } from './composeRecipientUtils';

type SendBulkSimpleOutboundDialogProps = {
  open: boolean;
  recipients: ComposeRecipient[];
  onClose: () => void;
  onSuccess: (message: string) => void;
};

type RecipientSendResult = {
  clientId: number;
  clientName: string;
  to: string;
  ok: boolean;
  message: string;
};

export const SendBulkSimpleOutboundDialog = ({
  open,
  recipients,
  onClose,
  onSuccess,
}: SendBulkSimpleOutboundDialogProps) => {
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ current: number; total: number } | null>(null);
  const [results, setResults] = useState<RecipientSendResult[] | null>(null);
  const [recipientsExpanded, setRecipientsExpanded] = useState(true);

  const [sendEmail] = useSendSimpleOutboundMutation();
  const [fetchStatus] = useLazyGetSimpleOutboundStatusQuery();
  const { data: configuredBcc = '' } = useGetConfiguredFinanceBccQuery(undefined, {
    skip: !open,
  });

  const recipientCount = recipients.length;
  const addressCount = useMemo(() => countRecipientAddresses(recipients), [recipients]);
  const recipientsSummary = formatRecipientsSummary(recipientCount, addressCount);
  const title =
    recipientCount === 1
      ? `Send email — ${recipients[0]?.clientName ?? 'client'}`
      : `Send email — ${recipientsSummary}`;
  const failedResults = useMemo(() => (results ?? []).filter((item) => !item.ok), [results]);
  const hasFinishedWithFailures = failedResults.length > 0;

  const progressPercent = useMemo(() => {
    if (!sendProgress || sendProgress.total <= 0) {
      return 0;
    }
    return Math.round((sendProgress.current / sendProgress.total) * 100);
  }, [sendProgress]);

  const handleClose = () => {
    if (isSending) {
      return;
    }
    onClose();
  };

  const handleSend = async () => {
    setLocalError(null);
    setResults(null);

    if (recipients.length === 0) {
      setLocalError('No recipients selected.');
      return;
    }

    if (!subject.trim()) {
      setLocalError('Subject is required.');
      return;
    }

    const missingTo = recipients.filter((recipient) => !recipient.to.trim());
    if (missingTo.length > 0) {
      setLocalError(
        `Missing recipient address for: ${missingTo.map((item) => item.clientName).join(', ')}.`
      );
      return;
    }

    setIsSending(true);
    setSendProgress({ current: 0, total: recipients.length });

    const sendResults: RecipientSendResult[] = [];

    try {
      for (let index = 0; index < recipients.length; index += 1) {
        const recipient = recipients[index];
        setSendProgress({ current: index, total: recipients.length });

        try {
          const queued = await sendEmail({
            clientId: recipient.clientId,
            to: recipient.to.trim(),
            cc: cc.trim(),
            subject: subject.trim(),
            message,
            files,
          }).unwrap();

          const result = await waitForSimpleOutboundResult(queued.historyId, async (historyId) => {
            const status = await fetchStatus(historyId).unwrap();
            return status;
          });

          const failed = (result.status || '').toLowerCase() === 'failed';
          sendResults.push({
            clientId: recipient.clientId,
            clientName: recipient.clientName,
            to: recipient.to,
            ok: !failed,
            message: failed
              ? result.message || 'Email was not sent.'
              : result.message || SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE,
          });
        } catch (error) {
          sendResults.push({
            clientId: recipient.clientId,
            clientName: recipient.clientName,
            to: recipient.to,
            ok: false,
            message: extractErrorMessage(
              error,
              'Email was not sent. Please try again or contact support'
            ),
          });
        }

        setSendProgress({ current: index + 1, total: recipients.length });
      }

      setResults(sendResults);

      const successCount = sendResults.filter((item) => item.ok).length;
      const failureCount = sendResults.length - successCount;

      if (successCount > 0) {
        invalidateGridDataCache(store.dispatch);
      }

      if (failureCount === 0) {
        onSuccess(
          successCount === 1
            ? sendResults[0]?.message || SIMPLE_OUTBOUND_SEND_SUCCESS_MESSAGE
            : `${successCount} emails were successfully sent`
        );
        onClose();
        return;
      }

      if (successCount === 0) {
        setLocalError(`None of the ${sendResults.length} emails were sent. See details below.`);
        return;
      }

      setLocalError(`${successCount} sent, ${failureCount} could not be sent. See details below.`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperComponent={DraggableResizablePaper}
      aria-labelledby={DRAGGABLE_DIALOG_TITLE_ID}
    >
      <DialogTitle id={DRAGGABLE_DIALOG_TITLE_ID} sx={{ cursor: 'move', userSelect: 'none' }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {localError && <Alert severity="error">{localError}</Alert>}

          {isSending && sendProgress && (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.75 }}>
                Sending {Math.min(sendProgress.current + 1, sendProgress.total)} of{' '}
                {sendProgress.total}…
              </Typography>
              <LinearProgress variant="determinate" value={progressPercent} />
            </Box>
          )}

          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.75 }}
            >
              <Typography variant="body2" fontWeight={700}>
                Recipients ({recipientsSummary})
              </Typography>
              <Button
                size="small"
                onClick={() => setRecipientsExpanded((value) => !value)}
                endIcon={recipientsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: 'none' }}
              >
                {recipientsExpanded ? 'Hide' : 'Show'}
              </Button>
            </Stack>
            <Collapse in={recipientsExpanded}>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {recipients.map((recipient) => (
                  <Chip
                    key={`${recipient.clientId}-${recipient.to}`}
                    size="small"
                    label={`${recipient.clientName}: ${recipient.to}`}
                    sx={{ maxWidth: '100%' }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Each client gets a separate outbound email with only their address in To.
              </Typography>
            </Collapse>
          </Box>

          <FinanceEmailComposeFields
            cc={cc}
            bcc={configuredBcc}
            subject={subject}
            message={message}
            onCcChange={setCc}
            onSubjectChange={setSubject}
            onMessageChange={setMessage}
            disabled={isSending || hasFinishedWithFailures}
            ccLabel="CC (shared)"
            ccHelperText="Applied to every email. Separate multiple addresses with comma or semicolon."
            bccHelperText="Configured in Umbraco. Applied automatically to every email on send."
          />

          <FinanceEmailComposeAttachments
            files={files}
            onChange={setFiles}
            disabled={isSending || hasFinishedWithFailures}
          />

          {hasFinishedWithFailures && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                Could not send to
              </Typography>
              <Stack spacing={0.5}>
                {failedResults.map((item) => (
                  <Typography key={item.clientId} variant="body2">
                    {item.clientName} ({item.to}): {item.message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSending}>
          {hasFinishedWithFailures ? 'Close' : 'Cancel'}
        </Button>
        {!hasFinishedWithFailures && (
          <Button
            variant="contained"
            onClick={() => void handleSend()}
            disabled={isSending || !subject.trim() || recipients.length === 0}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isSending ? 'Sending...' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SendBulkSimpleOutboundDialog;
